import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiGet, apiDelete, apiPost } from "../../utils/api";
import {
  PageHeader, Spinner, Card, CardBody, Badge,
  TableWrapper, Table, Th, Td, Button, Input, Select,
} from "../../components/ui";
import { useModal } from "../../context/ModalContext";
import {
  FaDatabase, FaTable, FaSearch, FaTrashAlt, FaEye, FaColumns,
  FaPlay, FaExclamationTriangle, FaHistory, FaSortUp, FaSortDown,
  FaChevronLeft, FaChevronRight, FaCopy, FaDownload, FaCode,
} from "react-icons/fa";

// ─── Helpers ────────────────────────────────────────────────────

function truncate(val, len = 50) {
  if (val == null) return "";
  const s = typeof val === "string" ? val : JSON.stringify(val);
  return s.length > len ? s.slice(0, len) + "..." : s;
}

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return String(d); }
}

function isDateLike(val) {
  if (typeof val !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}T/.test(val);
}

function isJsonLike(val) {
  if (typeof val !== "string") return typeof val === "object" && val !== null;
  return (val.startsWith("{") || val.startsWith("["));
}

function formatCell(val) {
  if (val === null || val === undefined) return <span className="text-ink-faint italic">null</span>;
  if (typeof val === "boolean") return val ? "true" : "false";
  if (isDateLike(val)) return fmtDate(val);
  if (typeof val === "object") return truncate(JSON.stringify(val), 50);
  return truncate(String(val), 50);
}

const HISTORY_KEY = "walytic_sql_history";

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); }
  catch { return []; }
}

function saveHistory(list) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 10)));
}

const SQL_SHORTCUTS = [
  { label: "All tables", sql: "SELECT tablename FROM pg_tables WHERE schemaname = 'public' ORDER BY tablename" },
  { label: "Table sizes", sql: "SELECT relname AS table, pg_size_pretty(pg_total_relation_size(relid)) AS size FROM pg_catalog.pg_statio_user_tables ORDER BY pg_total_relation_size(relid) DESC" },
  { label: "Active sessions", sql: "SELECT pid, state, query, query_start FROM pg_stat_activity WHERE state = 'active'" },
  { label: "Recent messages", sql: 'SELECT id, "from", "to", body, "createdAt" FROM messages ORDER BY "createdAt" DESC LIMIT 25' },
];

// ─── Tabs ───────────────────────────────────────────────────────

const TABS = [
  { id: "tables", label: "Tables", icon: FaDatabase },
  { id: "browse", label: "Browse", icon: FaTable },
  { id: "sql", label: "SQL", icon: FaCode },
];

// ─── Main Component ─────────────────────────────────────────────

export default function AdminDataManagement() {
  const modal = useModal();
  const [activeTab, setActiveTab] = useState("tables");

  // Tables tab
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tableFilter, setTableFilter] = useState("");

  // Browse tab
  const [browseTable, setBrowseTable] = useState("");
  const [browseSchema, setBrowseSchema] = useState([]);
  const [browseData, setBrowseData] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseLimit, setBrowseLimit] = useState(25);
  const [browseSort, setBrowseSort] = useState("id");
  const [browseSortDir, setBrowseSortDir] = useState("desc");
  const [browseSearch, setBrowseSearch] = useState("");
  const searchTimer = useRef(null);

  // SQL tab
  const [sqlText, setSqlText] = useState("");
  const [sqlRunning, setSqlRunning] = useState(false);
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlError, setSqlError] = useState("");
  const [sqlHistory, setSqlHistory] = useState(loadHistory);

  // ─── Fetch tables ───────────────────────────────────────────

  const fetchTables = useCallback(async () => {
    try {
      setTablesLoading(true);
      const res = await apiGet("/api/admin/db/tables");
      if (res?.data) setTables(res.data);
    } catch (err) {
      console.error("Failed to load tables", err);
    } finally {
      setTablesLoading(false);
    }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  // ─── Fetch schema ───────────────────────────────────────────

  const fetchSchema = useCallback(async (table) => {
    if (!table) { setBrowseSchema([]); return; }
    try {
      const res = await apiGet(`/api/admin/db/tables/${table}/schema`);
      if (res?.data) setBrowseSchema(res.data);
    } catch { setBrowseSchema([]); }
  }, []);

  // ─── Fetch rows ─────────────────────────────────────────────

  const fetchRows = useCallback(async (table, page, limit, sort, dir, search) => {
    if (!table) return;
    try {
      setBrowseLoading(true);
      const params = new URLSearchParams({
        page: String(page), limit: String(limit),
        sort, dir,
      });
      if (search) params.set("search", search);
      const res = await apiGet(`/api/admin/db/tables/${table}/rows?${params}`);
      if (res?.data) setBrowseData(res.data);
    } catch (err) {
      console.error("Browse fetch failed", err);
      setBrowseData(null);
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  // ─── Browse table change ────────────────────────────────────

  const openBrowse = useCallback((tableName) => {
    setBrowseTable(tableName);
    setBrowsePage(1);
    setBrowseSort("id");
    setBrowseSortDir("desc");
    setBrowseSearch("");
    setBrowseData(null);
    setActiveTab("browse");
    fetchSchema(tableName);
    fetchRows(tableName, 1, browseLimit, "id", "desc", "");
  }, [fetchSchema, fetchRows, browseLimit]);

  const refreshBrowse = useCallback(() => {
    if (browseTable) fetchRows(browseTable, browsePage, browseLimit, browseSort, browseSortDir, browseSearch);
  }, [browseTable, browsePage, browseLimit, browseSort, browseSortDir, browseSearch, fetchRows]);

  // ─── Truncate / Drop / Delete row ──────────────────────────

  const handleTruncate = (tableName) => {
    modal.confirm({
      title: `Truncate "${tableName}"?`,
      message: "This will delete ALL rows in this table. The table structure stays intact.",
      confirmText: "Truncate",
      variant: "danger",
      onConfirm: async () => {
        try {
          await apiPost(`/api/admin/db/tables/${tableName}/truncate`);
          fetchTables();
          if (browseTable === tableName) refreshBrowse();
        } catch (err) {
          console.error("Truncate failed", err);
        }
      },
    });
  };

  const handleDrop = (tableName) => {
    modal.confirm({
      title: `Drop "${tableName}"?`,
      message: "This permanently removes the table and all its data. This cannot be undone.",
      confirmText: "Drop Table",
      variant: "danger",
      onConfirm: () => {
        modal.confirm({
          title: "Are you absolutely sure?",
          message: `Type the table name to confirm: ${tableName}`,
          confirmText: "Yes, Drop It",
          variant: "danger",
          onConfirm: async () => {
            try {
              await apiDelete(`/api/admin/db/tables/${tableName}`);
              fetchTables();
              if (browseTable === tableName) {
                setBrowseTable("");
                setBrowseData(null);
              }
            } catch (err) {
              console.error("Drop failed", err);
            }
          },
        });
      },
    });
  };

  const handleDeleteRow = (tableName, rowId) => {
    modal.confirm({
      title: "Delete this row?",
      message: `Row ID: ${rowId} from "${tableName}"`,
      confirmText: "Delete",
      variant: "danger",
      onConfirm: async () => {
        try {
          await apiDelete(`/api/admin/db/tables/${tableName}/rows/${rowId}`);
          refreshBrowse();
          fetchTables();
        } catch (err) {
          console.error("Row delete failed", err);
        }
      },
    });
  };

  // ─── SQL execution ──────────────────────────────────────────

  const runSql = async (force = false) => {
    const sql = sqlText.trim();
    if (!sql) return;
    try {
      setSqlRunning(true);
      setSqlError("");
      setSqlResult(null);
      const res = await apiPost("/api/admin/db/query", { sql, force: force || undefined });
      if (res?.requiresForce) {
        setSqlError("This is a destructive query. Click 'Run (Force)' to execute it.");
        return;
      }
      if (res?.data) setSqlResult(res.data);
      // Save to history
      const next = [sql, ...sqlHistory.filter((h) => h !== sql)].slice(0, 10);
      setSqlHistory(next);
      saveHistory(next);
    } catch (err) {
      setSqlError(err?.message || err?.data?.error || "Query failed");
    } finally {
      setSqlRunning(false);
    }
  };

  const copyResultJson = () => {
    if (!sqlResult?.rows) return;
    navigator.clipboard.writeText(JSON.stringify(sqlResult.rows, null, 2));
  };

  // ─── Sort handler ───────────────────────────────────────────

  const handleSort = (col) => {
    let dir = "asc";
    if (browseSort === col && browseSortDir === "asc") dir = "desc";
    setBrowseSort(col);
    setBrowseSortDir(dir);
    setBrowsePage(1);
    fetchRows(browseTable, 1, browseLimit, col, dir, browseSearch);
  };

  // ─── Debounced search ──────────────────────────────────────

  const handleSearchChange = (val) => {
    setBrowseSearch(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setBrowsePage(1);
      fetchRows(browseTable, 1, browseLimit, browseSort, browseSortDir, val);
    }, 400);
  };

  // ─── Filtered tables ───────────────────────────────────────

  const filteredTables = tableFilter
    ? tables.filter((t) => t.name.toLowerCase().includes(tableFilter.toLowerCase()))
    : tables;

  // ─── Render ─────────────────────────────────────────────────

  return (
    <div>
      <PageHeader title="Database" subtitle="Adminer-style database management" />

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-surface-border mb-6 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                active
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-ink-secondary hover:text-ink-primary hover:border-surface-border"
              }`}
            >
              <Icon size={13} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ══════════════ TABLES TAB ══════════════ */}
      {activeTab === "tables" && (
        <div>
          {/* Search + count */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <FaSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                placeholder="Filter tables..."
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                className="input pl-8 text-sm"
              />
            </div>
            <span className="text-xs text-ink-secondary">
              {filteredTables.length} table{filteredTables.length !== 1 ? "s" : ""}
            </span>
          </div>

          {tablesLoading ? (
            <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredTables.map((t) => (
                <Card key={t.name} className="hover:border-blue-300 transition-colors">
                  <CardBody className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <button
                        onClick={() => openBrowse(t.name)}
                        className="font-mono text-sm font-semibold text-ink-primary hover:text-blue-600 transition-colors text-left truncate"
                        title={t.name}
                      >
                        {t.name}
                      </button>
                      <Badge variant="muted" className="ml-2 text-2xs font-mono flex-shrink-0">
                        {(t.rows ?? 0).toLocaleString()} rows
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-3">
                      <button
                        onClick={() => openBrowse(t.name)}
                        title="Browse rows"
                        className="p-1.5 rounded text-ink-muted hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <FaEye size={12} />
                      </button>
                      <button
                        onClick={() => {
                          setBrowseTable(t.name);
                          fetchSchema(t.name);
                          setActiveTab("browse");
                        }}
                        title="View schema"
                        className="p-1.5 rounded text-ink-muted hover:text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <FaColumns size={12} />
                      </button>
                      <button
                        onClick={() => handleTruncate(t.name)}
                        title="Truncate table"
                        className="p-1.5 rounded text-ink-muted hover:text-amber-600 hover:bg-amber-50 transition-colors"
                      >
                        <FaExclamationTriangle size={12} />
                      </button>
                      <button
                        onClick={() => handleDrop(t.name)}
                        title="Drop table"
                        className="p-1.5 rounded text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FaTrashAlt size={12} />
                      </button>
                    </div>
                  </CardBody>
                </Card>
              ))}
              {filteredTables.length === 0 && (
                <p className="col-span-full text-center text-sm text-ink-muted py-12">No tables found.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ BROWSE TAB ══════════════ */}
      {activeTab === "browse" && (
        <div>
          {/* Table selector + search */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="min-w-[200px]">
              <label className="block text-xs text-ink-secondary mb-1">Table</label>
              <select
                value={browseTable}
                onChange={(e) => {
                  const t = e.target.value;
                  if (t) openBrowse(t); else { setBrowseTable(""); setBrowseData(null); setBrowseSchema([]); }
                }}
                className="input text-sm font-mono"
              >
                <option value="">Select a table</option>
                {tables.map((t) => (
                  <option key={t.name} value={t.name}>{t.name} ({(t.rows ?? 0).toLocaleString()})</option>
                ))}
              </select>
            </div>

            {browseTable && (
              <>
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                  <FaSearch size={12} className="absolute left-3 top-[calc(50%+8px)] -translate-y-1/2 text-ink-faint" />
                  <label className="block text-xs text-ink-secondary mb-1">Search</label>
                  <input
                    type="text"
                    placeholder="Search rows..."
                    value={browseSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="input pl-8 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-ink-secondary mb-1">Rows/page</label>
                  <select
                    value={browseLimit}
                    onChange={(e) => {
                      const lim = Number(e.target.value);
                      setBrowseLimit(lim);
                      setBrowsePage(1);
                      fetchRows(browseTable, 1, lim, browseSort, browseSortDir, browseSearch);
                    }}
                    className="input text-sm w-20"
                  >
                    {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Schema summary */}
          {browseSchema.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-1.5">
              {browseSchema.map((col) => (
                <span
                  key={col.column_name}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-surface-bg border border-surface-border text-2xs font-mono"
                  title={`${col.data_type}${col.is_nullable === "YES" ? ", nullable" : ""}${col.column_default ? `, default: ${col.column_default}` : ""}`}
                >
                  <span className="text-ink-primary">{col.column_name}</span>
                  <span className="text-ink-faint">{col.data_type}{col.character_maximum_length ? `(${col.character_maximum_length})` : ""}</span>
                </span>
              ))}
            </div>
          )}

          {/* Data table */}
          {browseLoading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : browseData ? (
            <>
              {/* Row count */}
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-ink-secondary">
                  Showing {((browseData.page - 1) * browseData.limit) + 1}-{Math.min(browseData.page * browseData.limit, browseData.total)} of {browseData.total.toLocaleString()} rows
                </p>
              </div>

              <TableWrapper className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      {browseData.columns?.map((col) => {
                        const schemaCol = browseSchema.find((s) => s.column_name === col);
                        const isSorted = browseSort === col;
                        return (
                          <Th
                            key={col}
                            className="cursor-pointer select-none whitespace-nowrap font-mono text-xs"
                            onClick={() => handleSort(col)}
                          >
                            <span className="inline-flex items-center gap-1">
                              {col}
                              {schemaCol && (
                                <span className="text-2xs font-normal text-ink-faint">
                                  {schemaCol.data_type}
                                </span>
                              )}
                              {isSorted && (
                                browseSortDir === "asc"
                                  ? <FaSortUp size={10} className="text-blue-500" />
                                  : <FaSortDown size={10} className="text-blue-500" />
                              )}
                            </span>
                          </Th>
                        );
                      })}
                      <Th className="w-10"></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {browseData.rows?.map((row, i) => (
                      <tr key={row.id ?? i} className="hover:bg-surface-bg transition-colors">
                        {browseData.columns?.map((col) => (
                          <Td key={col} className="font-mono text-xs max-w-[300px] truncate" title={row[col] != null ? String(row[col]) : ""}>
                            {formatCell(row[col])}
                          </Td>
                        ))}
                        <Td>
                          {row.id != null && (
                            <button
                              onClick={() => handleDeleteRow(browseTable, row.id)}
                              className="p-1 rounded text-ink-muted hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete row"
                            >
                              <FaTrashAlt size={11} />
                            </button>
                          )}
                        </Td>
                      </tr>
                    ))}
                    {browseData.rows?.length === 0 && (
                      <tr>
                        <td colSpan={999} className="text-center text-sm text-ink-muted py-8">
                          No rows found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </TableWrapper>

              {/* Pagination */}
              {browseData.pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-ink-secondary">
                    Page {browseData.page} of {browseData.pages}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={browseData.page <= 1}
                      onClick={() => {
                        const p = browsePage - 1;
                        setBrowsePage(p);
                        fetchRows(browseTable, p, browseLimit, browseSort, browseSortDir, browseSearch);
                      }}
                      className="p-1.5 rounded border border-surface-border text-ink-secondary hover:bg-surface-bg disabled:opacity-40 transition-colors"
                    >
                      <FaChevronLeft size={11} />
                    </button>
                    <button
                      disabled={browseData.page >= browseData.pages}
                      onClick={() => {
                        const p = browsePage + 1;
                        setBrowsePage(p);
                        fetchRows(browseTable, p, browseLimit, browseSort, browseSortDir, browseSearch);
                      }}
                      className="p-1.5 rounded border border-surface-border text-ink-secondary hover:bg-surface-bg disabled:opacity-40 transition-colors"
                    >
                      <FaChevronRight size={11} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : browseTable ? (
            <p className="text-sm text-ink-muted text-center py-12">Loading table data...</p>
          ) : (
            <p className="text-sm text-ink-muted text-center py-12">Select a table to browse its rows.</p>
          )}
        </div>
      )}

      {/* ══════════════ SQL TAB ══════════════ */}
      {activeTab === "sql" && (
        <div>
          {/* Query shortcuts */}
          <div className="flex flex-wrap gap-2 mb-3">
            {SQL_SHORTCUTS.map((s) => (
              <button
                key={s.label}
                onClick={() => setSqlText(s.sql)}
                className="px-2.5 py-1 text-2xs rounded-full border border-surface-border text-ink-secondary hover:bg-surface-bg hover:text-ink-primary transition-colors"
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* SQL editor */}
          <textarea
            value={sqlText}
            onChange={(e) => setSqlText(e.target.value)}
            placeholder="SELECT * FROM users LIMIT 10;"
            rows={6}
            className="input font-mono text-sm w-full resize-y mb-3"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                runSql(false);
              }
            }}
          />

          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="primary"
              size="sm"
              loading={sqlRunning}
              onClick={() => runSql(false)}
              disabled={!sqlText.trim()}
            >
              <FaPlay size={10} className="mr-1.5" /> Run Query
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={sqlRunning}
              onClick={() => runSql(true)}
              disabled={!sqlText.trim()}
            >
              <FaExclamationTriangle size={10} className="mr-1.5" /> Run (Force)
            </Button>
            {sqlResult?.rows && (
              <button
                onClick={copyResultJson}
                className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs rounded border border-surface-border text-ink-secondary hover:bg-surface-bg transition-colors"
              >
                <FaCopy size={10} /> Copy as JSON
              </button>
            )}
          </div>

          {/* Error */}
          {sqlError && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {sqlError}
            </div>
          )}

          {/* Results */}
          {sqlResult && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                {sqlResult.rowCount != null && (
                  <span className="text-xs text-ink-secondary">
                    {sqlResult.rows ? `${sqlResult.rows.length} row${sqlResult.rows.length !== 1 ? "s" : ""}` : `${sqlResult.rowsAffected ?? 0} rows affected`}
                  </span>
                )}
                {sqlResult.duration != null && (
                  <span className="text-xs text-ink-faint">{sqlResult.duration}ms</span>
                )}
              </div>

              {sqlResult.rows && sqlResult.columns ? (
                <TableWrapper className="overflow-x-auto">
                  <Table>
                    <thead>
                      <tr>
                        {sqlResult.columns.map((c) => (
                          <Th key={c} className="font-mono text-xs whitespace-nowrap">{c}</Th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.map((row, i) => (
                        <tr key={i} className="hover:bg-surface-bg transition-colors">
                          {sqlResult.columns.map((c) => (
                            <Td key={c} className="font-mono text-xs max-w-[300px] truncate" title={row[c] != null ? String(row[c]) : ""}>
                              {formatCell(row[c])}
                            </Td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrapper>
              ) : sqlResult.rowsAffected != null ? (
                <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                  {sqlResult.rowsAffected} row{sqlResult.rowsAffected !== 1 ? "s" : ""} affected
                  {sqlResult.duration != null && <span className="ml-2 text-green-500">({sqlResult.duration}ms)</span>}
                </div>
              ) : null}
            </div>
          )}

          {/* Query history */}
          {sqlHistory.length > 0 && (
            <div className="mt-6">
              <p className="flex items-center gap-1.5 text-xs text-ink-secondary mb-2">
                <FaHistory size={10} /> Recent queries
              </p>
              <div className="flex flex-wrap gap-1.5">
                {sqlHistory.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setSqlText(q)}
                    className="px-2.5 py-1 text-2xs font-mono rounded bg-surface-bg border border-surface-border text-ink-secondary hover:text-ink-primary hover:border-blue-300 transition-colors truncate max-w-[280px]"
                    title={q}
                  >
                    {truncate(q, 40)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
